import React, { forwardRef, useEffect } from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import PlaySmallIcon from '../../assets/icons/PlaySmallIcon';
import ProgressLine from '../../pages/AnalyticsPage/components/ProgressLine';
import { colorsPalette } from '../../theme';
import { PATHWAY_STATUSES } from '../../types';
import { IKImage } from '../IKImage';
import useStyles from './styles';

interface VideoListItemProps {
  title?: string;
  imageUrl?: string | null;
  progress?: number;
  current?: number | string;
  total?: number | string;
  url?: string;
  withPlayIcon?: boolean;
  pattern?: (current: number | string, total: number | string) => string;
  wrapperClass?: string;
  testScore?: number | null;
  hasQuizzes?: boolean;
  pathwayStatus?: string | null;
  'data-testid'?: string;
}

const VideoListItem: React.FC<VideoListItemProps> = forwardRef(
  (
    {
      title,
      imageUrl,
      current,
      total,
      url,
      progress,
      withPlayIcon = false,
      pattern,
      wrapperClass = '',
      hasQuizzes,
      pathwayStatus,
      testScore,
      ...rest
    },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const progressString =
      pattern && current && total ? pattern(current, total) : `${current} / ${total}`;
    const hasCurrent = current || current === 0;

    const classes = useStyles(hasCurrent)();

    const { t } = useTranslation();

    useEffect(() => {
      if (window?.objectFitPolyfill) {
        window.objectFitPolyfill();
      }
    }, []);

    return (
      <div
        ref={ref}
        className={cx(classes.wrapper, wrapperClass)}
        data-testid={rest['data-testid']}
      >
        <div className={classes.itemLeftBlock}>
          <div className={classes.itemLeftBlockInner}>
            {imageUrl && (
              <div className={classes.videoItemPreviewWrapper}>
                <IKImage
                  path={imageUrl}
                  alt={title}
                  srcset={[{ width: 62, height: 43, crop: 'maintain_ratio' }]}
                  sizes="62px"
                  draggable={false}
                  data-object-fit="cover"
                />
                {withPlayIcon && (
                  <PlaySmallIcon color={colorsPalette.white} className={classes.playIcon} />
                )}
              </div>
            )}
            <div>
              <p className={classes.tableTitle}>
                {url && (
                  <Link data-testid="videoUrl" to={url} className={classes.itemHref}>
                    {title}
                  </Link>
                )}
                {!url && title}
              </p>
              {pathwayStatus === PATHWAY_STATUSES.WATCHED && hasQuizzes && (
                <p className={classes.helperText}>{t('page.pathway.takeTest')}</p>
              )}
              {pathwayStatus === PATHWAY_STATUSES.HAS_TEST_SCORE && (
                <p className={classes.helperText}>
                  <span className={classes.redLabel}>
                    {t('block.analyticsTable.testScore')} {testScore}%
                  </span>{' '}
                  {t('page.pathway.retakeTest')}
                </p>
              )}
              {pathwayStatus === PATHWAY_STATUSES.COMPLETED && (
                <p className={classes.helperText}>
                  <span className={classes.greenLabel}>
                    {t('block.analyticsTable.testScore')} {testScore}%
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>

        {(progress || progress === 0) && (
          <div className={classes.progressLineWrapper}>
            <ProgressLine
              value={Math.floor(progress)}
              progressString={progressString}
              completed={pathwayStatus === PATHWAY_STATUSES.COMPLETED}
            />
          </div>
        )}
        {!hasCurrent && <p className={classes.videoItemDuration}>{total}</p>}
      </div>
    );
  },
);

export default VideoListItem;
